import { useState } from "react";
import type { ReactNode } from "react";
import { CalendarDays, Check, ChevronRight, FileText, House, Leaf, MapPin, Pencil, Printer, Sparkles, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOAN_LABELS } from "@/utils/deals.utils";
import DealSummaryEditor from "./DealSummaryEditor";
import { DueDiligenceValue, SpecRow, dash } from "./DealSummaryDisplay";
import { cn, formatUtcDate } from "@/utils/common.utils";
import { daysBetweenIso, formatDealDateTime, formatInspectionWindow, formatMoney } from "@/utils/deals.utils";
import { Financing, PurchaseMethod } from "@/enum/common.enum";
import type { ContractTerms, Deal, DealDetail } from "@/types/deals.types";

interface DealSummaryProps {
  deal: DealDetail;
  canEdit?: boolean;
  onSaved?: (fresh: Deal) => void;
  tone?: string;
  summary?: unknown;
}

type StepState = "done" | "current" | "future";
interface PathStep {
  key: string;
  label: string;
  date: string | null | undefined;
  /** Shown in the Next up bar when it carries a time the date alone does not. */
  when?: string | null;
  state: StepState;
}

/**
 * Path to Closing, driven only by dates already on the contract. Nothing here
 * is a second progress model to keep in sync: a step is done once its date has
 * passed, and the first step that is not done is the current one. The card is
 * only mounted under contract, so the Contract step is always done. It carries
 * no date: no signing date is captured, and `under_contract_at` is only when
 * the stage moved in Stoop, which can land after the real milestones.
 */
function buildPathToClosing(ct: ContractTerms): PathStep[] {
  const now = new Date().toISOString();
  const passed = (iso: string | null | undefined) => {
    const days = daysBetweenIso(now, iso);
    return typeof days === "number" && days < 0;
  };
  const steps = [
    { key: "contract", label: "Contract", date: null, done: true },
    { key: "inspection", label: "Inspection", date: ct.inspection_date, done: passed(ct.inspection_date) },
    { key: "due_diligence", label: "Due diligence", date: ct.due_diligence_end_date, done: passed(ct.due_diligence_end_date) },
    { key: "closing", label: "Closing", date: ct.closing_date, when: ct.closing_datetime, done: passed(ct.closing_date) },
  ];
  const currentIndex = steps.findIndex((step) => !step.done);
  return steps.map(({ done, ...step }, index) => ({
    ...step,
    state: done ? "done" : index === currentIndex ? "current" : "future",
  }));
}

function PathToClosing({ steps, onOpenNext }: { steps: PathStep[]; onOpenNext?: () => void }) {
  const next = steps.find((step) => step.state === "current");
  const nextWhen = next?.when ? formatDealDateTime(next.when) : formatUtcDate(next?.date, {}, "Date not set");
  const nextBody = next && (
    <>
      <CalendarDays className="size-4 shrink-0 text-dash-muted" aria-hidden />
      <span className="min-w-0 flex-1 truncate text-left text-dash-ink">
        Next up <span className="mx-1 text-dash-faint">·</span>
        <span className="font-semibold">{next.label}</span>
        <span className="mx-1 text-dash-faint">·</span>
        <span className="text-xs text-dash-muted">{nextWhen}</span>
      </span>
      {onOpenNext && <ChevronRight className="size-4 shrink-0 text-dash-muted" aria-hidden />}
    </>
  );
  const nextCls = "mt-6 flex w-full items-center gap-3 rounded-lg bg-dash-hover px-4 py-3 text-sm";

  return (
    <div className="rounded-xl border border-dash-border bg-white p-4 sm:p-6" data-testid="ds-path-to-closing">
      <h3 className="text-xl font-semibold text-dash-ink">Path to Closing</h3>
      {/* Vertical on phones, where four labels cannot share one row; horizontal from sm up. */}
      <ol className="mt-6 flex flex-col sm:flex-row sm:items-start">
        {steps.map((step, index) => {
          const last = index === steps.length - 1;
          return (
            <li key={step.key} className={cn("flex flex-col sm:flex-row sm:items-start", !last && "sm:flex-1")} data-testid={`ds-path-${step.key}`} data-state={step.state}>
              <div className={cn("flex items-center gap-3 sm:shrink-0 sm:flex-col sm:gap-0",
                index === 0 ? "sm:items-start sm:text-left" : last ? "sm:items-end sm:text-right" : "sm:items-center sm:text-center")}>
                <span
                  className={cn("flex size-6 shrink-0 items-center justify-center rounded-full",
                    step.state === "done" && "bg-dash-greenDeep text-white",
                    step.state === "current" && "border-2 border-dash-greenDeep bg-white",
                    step.state === "future" && "border-2 border-dash-border bg-white")}
                  aria-hidden
                >
                  {step.state === "done" && <Check className="size-3.5" />}
                  {step.state === "current" && <span className="size-2.5 rounded-full bg-dash-greenDeep" />}
                </span>
                <span className="flex-1 whitespace-nowrap text-sm text-dash-ink sm:mt-3 sm:flex-none">{step.label}</span>
                {step.key !== "contract" && (
                  <span className="whitespace-nowrap text-sm text-dash-muted sm:mt-1">{formatUtcDate(step.date, {}, "—")}</span>
                )}
              </div>
              {!last && (
                <span
                  className={cn("my-1 ml-[11px] h-5 w-0.5 rounded-full sm:mx-1 sm:my-0 sm:mt-[11px] sm:h-0.5 sm:w-auto sm:flex-1",
                    steps[index + 1].state === "future" ? "bg-dash-border" : "bg-dash-greenDeep")}
                  aria-hidden
                />
              )}
            </li>
          );
        })}
      </ol>
      {next && (onOpenNext ? (
        <button type="button" onClick={onOpenNext} className={cn(nextCls, "transition-colors hover:bg-dash-chip")}
          aria-label={`Edit ${next.label} date`} data-testid="ds-next-up">
          {nextBody}
        </button>
      ) : (
        <div className={nextCls} data-testid="ds-next-up">{nextBody}</div>
      ))}
    </div>
  );
}

function DetailCard({ icon, title, children, testid }: { icon: ReactNode; title: string; children: ReactNode; testid: string }) {
  return (
    <div className="rounded-xl border border-dash-border bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-3 border-b border-dash-borderSoft pb-3">
        <span className="text-dash-ink" aria-hidden>{icon}</span>
        <h3 className="text-base font-semibold text-dash-ink">{title}</h3>
      </div>
      <dl className="mt-1">{children}</dl>
    </div>
  );
}

function HeroRow({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3 py-1.5 text-sm">
      <dt className="text-white/75">{k}</dt>
      <dd className="break-words font-semibold text-white">{v}</dd>
    </div>
  );
}

export default function DealSummary({ deal, canEdit, onSaved }: DealSummaryProps) {
  const [editing, setEditing] = useState(false);
  const ct = deal?.contract_terms || {};
  // `financing` only exists on the internal_full view; client/external payloads omit it.
  const dealFinancing = "financing" in deal ? deal.financing : undefined;
  const isCash = ct.purchase_method === PurchaseMethod.CASH || (!ct.purchase_method && dealFinancing === Financing.CASH);
  const empty =
    !ct.purchase_price &&
    !ct.closing_date &&
    !ct.due_diligence_end_date &&
    !ct.earnest_money_deposit &&
    !ct.financing_contingency_days &&
    !ct.financing_contingency_date &&
    !ct.purchase_method &&
    !ct.special_stipulations &&
    !ct.home_sale_contingency_enabled &&
    !ct.kickout_clause_enabled &&
    !ct.closing_address &&
    !ct.closing_datetime &&
    !ct.closing_contact_name &&
    !ct.closing_contact_phone &&
    !ct.earnest_money_holder &&
    !ct.option_fee_amount &&
    !ct.earnest_money_due_date &&
    !ct.option_fee_due_date &&
    !ct.seller_names &&
    !ct.buyer_names &&
    !ct.inspection_date &&
    !ct.inspection_company &&
    !ct.inspection_notes &&
    !ct.inspection_start_time &&
    !ct.inspection_end_time &&
    !ct.possession_datetime &&
    !ct.utility_connect_date &&
    !ct.utility_disconnect_date &&
    !ct.closing_law_firm &&
    !ct.listing_agent &&
    !ct.listing_brokerage &&
    !ct.coop_brokerage &&
    !ct.coop_agent &&
    !ct.further_considerations;

  const purchase = isCash ? "Cash" : ct.purchase_method || dealFinancing ? "Financed" : dash;
  const loan = isCash ? "Cash" : ct.loan_type ? LOAN_LABELS[ct.loan_type] : dash;
  const hasFinancingContingency = !isCash && (ct.financing_contingency_date || ct.financing_contingency_days != null);
  const contingencies = [
    hasFinancingContingency && "Financing",
    ct.home_sale_contingency_enabled && "Home sale",
    ct.kickout_clause_enabled && "Kickout",
  ].filter(Boolean).join(", ");
  const inspectionWindow = formatInspectionWindow(ct);
  const hasClosingDay = Boolean(ct.closing_address || ct.closing_datetime || ct.closing_contact_name || ct.closing_contact_phone);
  // Everything the mockup's three cards have no row for. Folded rather than
  // dropped: these are terms an agent typed, and the editor would otherwise be
  // the only place they could be read back.
  const hasMore = Boolean(
    hasFinancingContingency || ct.home_sale_contingency_enabled || ct.kickout_clause_enabled ||
    ct.option_fee_amount || ct.earnest_money_due_date || ct.option_fee_due_date || ct.inspection_company || ct.inspection_notes || inspectionWindow ||
    ct.possession_datetime || ct.utility_connect_date || ct.utility_disconnect_date ||
    ct.special_stipulations || ct.further_considerations || hasClosingDay,
  );

  return (
    <section data-testid="deal-summary-card" aria-label="Contract at a glance">
      <div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <h2 className="text-2xl font-semibold text-dash-ink">Contract at a Glance</h2>
        {(canEdit || !empty) && (
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && (
              <Button variant="dashboardOutline" size="sm" onClick={() => setEditing(true)} data-testid="deal-summary-edit-btn">
                <Pencil data-icon="inline-start" /> {empty ? "Add terms" : "Edit"}
              </Button>
            )}
            {!empty && (
              <Button
                variant="dashboardOutline"
                size="sm"
                onClick={() => window.open(`/deals/${deal.id}/summary/print`, "_blank", "noopener")}
                title="Open a print-friendly one-pager (Save as PDF from your browser's print dialog)"
                data-testid="deal-summary-print-btn"
              >
                <Printer data-icon="inline-start" /> Print / PDF
              </Button>
            )}
          </div>
        )}
      </div>

      {empty && !canEdit && (
        <div className="rounded-xl border border-dash-border bg-white p-6 text-sm text-dash-muted" data-testid="deal-summary-empty">
          Your agent hasn&apos;t published the contract terms yet. They&apos;ll appear here as soon as they do.
        </div>
      )}

      {empty && canEdit && (
        <div className="rounded-xl border border-dashed border-dash-border bg-white p-6 text-sm text-dash-muted" data-testid="deal-summary-empty-agent">
          No contract terms captured yet.{" "}
          <Button variant="seamless" onClick={() => setEditing(true)} className="text-dash-greenDeep underline underline-offset-4" data-testid="deal-summary-empty-cta">
            Add them now
          </Button>{" "}
          so your client sees the numbers that matter.
        </div>
      )}

      {!empty && (
        <>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,5fr)_minmax(0,8fr)]">
            <div className="relative overflow-hidden rounded-xl bg-dash-greenDeep p-5 text-white sm:p-6" data-testid="ds-contract-value">
              <Leaf className="pointer-events-none absolute -bottom-6 -right-6 size-32 text-white/[0.06]" aria-hidden />
              <div className="text-sm text-white/80">Contract value</div>
              <div className="mt-2 break-words text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="ds-purchase-price">
                {formatMoney(ct.purchase_price) || "—"}
              </div>
              <dl className="relative mt-6 border-t border-white/20 pt-4">
                <HeroRow k="Earnest money" v={formatMoney(ct.earnest_money_deposit) || "—"} />
                <HeroRow k="Purchase" v={purchase} />
                <HeroRow k="Loan" v={loan} />
              </dl>
            </div>
            <PathToClosing
              steps={buildPathToClosing(ct)}
              onOpenNext={canEdit ? () => setEditing(true) : undefined}
            />
          </div>

          <div className="mb-4 mt-8 flex items-center gap-4">
            <h2 className="shrink-0 text-2xl font-semibold text-dash-ink">Contract details</h2>
            <span className="h-px flex-1 bg-dash-border" aria-hidden />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <DetailCard icon={<FileText className="size-5" />} title="Key terms" testid="ds-key-terms">
              <SpecRow k="Purchase price" v={formatMoney(ct.purchase_price) || dash} testid="ds-key-price" />
              <SpecRow k="Earnest money" v={formatMoney(ct.earnest_money_deposit) || dash} testid="ds-earnest-money" />
              <SpecRow k="Loan type" v={loan} testid="ds-loan-type" />
              <SpecRow k="Contingencies" v={contingencies || "None"} testid="ds-contingencies" />
            </DetailCard>
            <DetailCard icon={<Users className="size-5" />} title="People" testid="ds-people">
              <SpecRow k="Seller" v={ct.seller_names || dash} testid="ds-seller-names" />
              <SpecRow k="Buyer" v={ct.buyer_names || dash} testid="ds-buyer-names" />
              <SpecRow k="Listing agent" v={ct.listing_agent || dash} testid="ds-listing-agent" />
              <SpecRow k="Buyer agent" v={ct.coop_agent || dash} testid="ds-coop-agent" />
            </DetailCard>
            <DetailCard icon={<House className="size-5" />} title="Other side" testid="ds-other-side">
              <SpecRow k="Listing brokerage" v={ct.listing_brokerage || dash} testid="ds-listing-brokerage" />
              <SpecRow k="Buyer brokerage" v={ct.coop_brokerage || dash} testid="ds-coop-brokerage" />
              <SpecRow k="Title company" v={ct.closing_law_firm || dash} testid="ds-closing-law-firm" />
              <SpecRow k="Escrow officer" v={ct.earnest_money_holder || dash} testid="ds-emd-holder" />
            </DetailCard>
          </div>

          {hasMore && (
            <details className="group mt-4 rounded-xl border border-dash-border bg-white" data-testid="ds-more-terms">
              <summary className="flex cursor-pointer list-none items-center gap-2 px-5 py-3.5 text-sm font-medium text-dash-ink [&::-webkit-details-marker]:hidden">
                <ChevronRight className="size-4 text-dash-faint transition-transform group-open:rotate-90" aria-hidden />
                More terms
              </summary>
              <div className="border-t border-dash-borderSoft px-5 pb-5">
                <dl className="grid grid-cols-1 gap-x-10 md:grid-cols-2">
                  {hasFinancingContingency && (
                    <SpecRow
                      k="Financing contingency"
                      v={ct.financing_contingency_date
                        ? `By ${formatUtcDate(ct.financing_contingency_date, { year: "numeric" }, "—")}`
                        : `${ct.financing_contingency_days} days`}
                      testid="ds-fin-contingency"
                    />
                  )}
                  {ct.home_sale_contingency_enabled && (
                    <SpecRow k="Home-sale contingency" v={ct.home_sale_contingency_address || "Yes"} testid="ds-hsc" />
                  )}
                  {ct.kickout_clause_enabled && (
                    <SpecRow k="Kickout clause" v={ct.kickout_clause_details || "Yes"} testid="ds-kickout" />
                  )}
                  <SpecRow k="Due diligence" v={<DueDiligenceValue iso={ct.due_diligence_end_date} />} testid="ds-dd-date" />
                  {!!ct.option_fee_amount && (
                    <SpecRow k="Option / DD fee" v={formatMoney(ct.option_fee_amount)} testid="ds-option-fee" />
                  )}
                  {ct.earnest_money_due_date && (
                    <SpecRow k="Earnest money due" v={formatUtcDate(ct.earnest_money_due_date, { year: "numeric" }, "—")} testid="ds-emd-due" />
                  )}
                  {ct.option_fee_due_date && (
                    <SpecRow k="DD money due" v={formatUtcDate(ct.option_fee_due_date, { year: "numeric" }, "—")} testid="ds-option-fee-due" />
                  )}
                  {ct.inspection_company && (
                    <SpecRow k="Inspection company" v={ct.inspection_company} testid="ds-inspection-company" />
                  )}
                  {inspectionWindow && (
                    <SpecRow k="Inspection window" v={inspectionWindow} testid="ds-inspection-window" />
                  )}
                  {ct.possession_datetime && (
                    <SpecRow k="Possession" v={formatDealDateTime(ct.possession_datetime)} testid="ds-possession" />
                  )}
                  {ct.utility_connect_date && (
                    <SpecRow k="Utilities connect" v={formatUtcDate(ct.utility_connect_date, { year: "numeric" }, "—")} testid="ds-utility-connect" />
                  )}
                  {ct.utility_disconnect_date && (
                    <SpecRow k="Utilities disconnect" v={formatUtcDate(ct.utility_disconnect_date, { year: "numeric" }, "—")} testid="ds-utility-disconnect" />
                  )}
                </dl>
                {ct.inspection_notes && (
                  <SpecRow k="Inspection notes" v={ct.inspection_notes} testid="ds-inspection-notes" />
                )}

                {ct.special_stipulations && (
                  <div className="mt-4 rounded-lg bg-dash-searchBg p-4" data-testid="ds-tile-notes">
                    <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-dash-muted">
                      <Sparkles className="size-3" aria-hidden /> Special stipulations
                    </div>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-dash-ink" data-testid="ds-stipulations">
                      {ct.special_stipulations}
                    </p>
                  </div>
                )}

                {ct.further_considerations && (
                  <div className="mt-4 rounded-lg bg-dash-searchBg p-4" data-testid="ds-tile-further">
                    <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-dash-muted">
                      <Sparkles className="size-3" aria-hidden /> Further considerations
                    </div>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-dash-ink" data-testid="ds-further">
                      {ct.further_considerations}
                    </p>
                  </div>
                )}

                {hasClosingDay && (
                  <div className="mt-4" data-testid="ds-closing-details">
                    <div className="flex items-center gap-1.5 pt-2 text-xs font-medium text-dash-muted">
                      <MapPin className="size-3" aria-hidden /> Closing day
                    </div>
                    <dl className="grid grid-cols-1 gap-x-10 md:grid-cols-2">
                      <SpecRow k="Address" v={ct.closing_address || dash} testid="ds-closing-address" />
                      <SpecRow k="When" v={ct.closing_datetime ? formatDealDateTime(ct.closing_datetime) : dash} testid="ds-closing-datetime" />
                      <SpecRow k="Contact" v={ct.closing_contact_name || dash} testid="ds-closing-contact-name" />
                      <SpecRow
                        k="Phone"
                        v={ct.closing_contact_phone
                          ? <a href={`tel:${ct.closing_contact_phone}`} className="text-dash-greenDeep hover:underline underline-offset-4">{ct.closing_contact_phone}</a>
                          : dash}
                        testid="ds-closing-contact-phone"
                      />
                    </dl>
                  </div>
                )}
              </div>
            </details>
          )}
        </>
      )}

      {editing && (
        <DealSummaryEditor
          deal={deal}
          ct={ct}
          onClose={() => setEditing(false)}
          onSaved={(fresh: Deal) => {
            setEditing(false);
            onSaved?.(fresh);
          }}
        />
      )}
    </section>
  );
}
